import React, { Component } from 'react';
import styled from 'styled-components';
import MaxWidthLayout from '@ui/templates/MaxWidthLayout';
import { CloseIcon } from '@ui/svgs/CloseIcon';
import { trackAnalytics, attachLocale, isSlug, makeExternalUrl, toSlug } from '../../../../lib/utils';
import { DOM_GLOBAL_BANNER } from '../../../../lib/constants';
import { getExpiryTimeout, isNotExpired } from '../../../../lib/helpers';
import SymbolFormat from '../shared/SymbolFormat';

const BannerOuterWrapper = styled.div`
  z-index: ${(p) => p.theme.elevation[2]};
  ${({ theme }) => theme['elevation-shadow'][2]}

  width: 100%;
  position: sticky;
  top: auto;
  bottom: 0;
  background-color: ${(p) => (p.bgColor ? p.bgColor : p.theme.c.bg.primary)};
  border-top: 1px solid ${({ theme }) => theme.c.border.secondary};
`;

const BannerWrapperDiv = styled.div`
  padding: ${(p) => p.theme.unit(3)} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BannerAnchor = styled.a`
  text-decoration: none;
  width: 100%;
  height: 100%;
  :hover {
    cursor: pointer;
  }
`;

const BannerAnchorImage = styled.img`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: -1;
  left: 0;
  padding: 0;
  object-fit: cover;
  :hover {
    cursor: pointer;
  }
`;

const TextDiv = styled.div`
  color: ${(p) => p.hexForFont || p.theme.c.text.primary};
`;

const HeaderTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.heading.lg}
`;

const SubHeaderTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.lg}
`;

const DesktopText = styled.div`
  display: none;
  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
    display: inline-block;
  }
`;

const MobileText = styled.div`
  display: inline-block;
  @media (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
    display: none;
  }
`;

const CloseButtonContainer = styled.div`
  display: flex;
`;

const CloseButton = styled.button`
  border: 0;
  margin: 0;
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  color: ${({ theme }) => theme.c.icon.interactive};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  cursor: pointer;

  :hover {
    color: ${({ theme }) => theme.c.icon['interactive-hover']};
  }
`;

const BannerWithImage = ({ children, url, dataTrackingId, dataTrack, resolvedBackgroundImage }) => {
  return (
    <BannerAnchor
      href={url}
      id={dataTrackingId}
      data-track-id={dataTrackingId}
      data-track-click={dataTrack}
    >
      {resolvedBackgroundImage && (
        <BannerAnchorImage src={resolvedBackgroundImage} alt="" />
      )}
      { children }
    </BannerAnchor>
  );
};

class GlobalBanner extends Component {
  constructor(props) {
    super(props);

    if (isNotExpired(this.props, 'startDate', 'endDate')) {
      this.state = { show: true };
    } else {
      this.state = { show: false };
    }

    this.hideBanner = this.hideBanner.bind(this);
  }

  componentDidMount() {
    if (this.props.endDate) {
      setTimeout(async () => {
        this.hideBanner();
      }, getExpiryTimeout(this.props.endDate));
    }
    if (this.state.show && document) {
      const html = document.documentElement;
      if (html) {
        html.classList.add('global-banner-padding');
      }
    }
  }

  hideBanner() {
    this.setState({ show: false });
    if (document) {
      const html = document.documentElement;
      if (html) {
        html.classList.remove('global-banner-padding');
      }
    }
  }

  render() {
    const {
      id,
      name,
      headerText,
      subHeaderText,
      mobileText,
      redirectUrl,
      hexForBackground,
      backgroundImageUrl,
      hexForFont,
      multiplierValue,
      tags,
      locale,
      backgroundImageAltText,
      accessibilityLabel,
      closeIconLabel = 'Close',
    } = this.props;

    if (!isNotExpired(this.props, 'startDate', 'endDate')) {
      return null;
    }

    if (!(headerText || hexForBackground || hexForFont)) {
      return null;
    }

    const resolvedBackgroundImage = makeExternalUrl(backgroundImageUrl);
    const resolvedRedirectUrl = isSlug(redirectUrl) ? attachLocale(redirectUrl, locale) : makeExternalUrl(redirectUrl);
    const resolvedSlug = toSlug(name);
    const track = trackAnalytics(DOM_GLOBAL_BANNER, `${resolvedSlug}`, resolvedRedirectUrl, tags);

    const isShowing = this.state.show;
    const onCloseHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hideBanner();
    };

    return isShowing && (
      <BannerOuterWrapper bgColor={hexForBackground} role="region" aria-label={accessibilityLabel}>
        <MaxWidthLayout key={`globalbanner-${id}`}>
          <BannerWrapperDiv>
            <BannerWithImage {...track} resolvedBackgroundImage={resolvedBackgroundImage}>
              <TextDiv hexForFont={hexForFont}>
                <DesktopText>
                  <HeaderTextDiv><SymbolFormat text={headerText} /></HeaderTextDiv>
                  <SubHeaderTextDiv><SymbolFormat text={subHeaderText} /></SubHeaderTextDiv>
                </DesktopText>
                <MobileText>
                  <HeaderTextDiv><SymbolFormat text={mobileText} /></HeaderTextDiv>
                </MobileText>
              </TextDiv>
            </BannerWithImage>
            <CloseButtonContainer>
              <CloseButton onClick={onCloseHandler} aria-label={closeIconLabel}>
                <CloseIcon title={closeIconLabel} />
              </CloseButton>
            </CloseButtonContainer>
          </BannerWrapperDiv>
        </MaxWidthLayout>
      </BannerOuterWrapper>
    );
  }
}

export default GlobalBanner;
